import type { RecentlyViewedContentType } from '@mfw/common';
import type { Locale } from '@mfw/translations';

import type { ApiNutritionPlan } from '../schemas/nutritionPlans.schema';
import type { ApiExercise } from '../schemas/exercises.schema';
import type { ApiWorkout } from '../schemas/workouts.schema';
import type { ApiProgram } from '../schemas/programs.schema';
import type { BaseApiResponse } from './types/api.types';
import type { ApiMeal } from '../schemas/meals.schema';

import { mapNutritionPlanFromApi } from './mappers/nutritionPlans.mappers';
import { mapExerciseFromApi } from './mappers/exercises.mappers';
import { mapWorkoutFromApi } from './mappers/workouts.mappers';
import { mapProgramFromApi } from './mappers/programs.mappers';
import { mapMealFromApi } from './mappers/meals.mappers';
import { get } from './axios';

export interface RecentlyViewedRecord<
  T extends ApiExercise | ApiMeal | ApiNutritionPlan | ApiProgram | ApiWorkout,
> {
  contentId: string;
  viewedAt: string;
  content: T;
  id: string;
}

type RecentlyViewedResponse = BaseApiResponse<
  RecentlyViewedRecord<
    ApiExercise | ApiMeal | ApiNutritionPlan | ApiProgram | ApiWorkout
  >[]
>;

export async function fetchRecentlyViewedContent({
  contentType,
  limit,
  locale,
  organizationId,
  assignedOnly,
}: {
  contentType: RecentlyViewedContentType;
  locale: Locale;
  limit: number;
  organizationId?: string;
  assignedOnly?: boolean;
}) {
  const response = await get<RecentlyViewedResponse>(
    `/v2${organizationId ? `/organizations/${organizationId}` : ''}/recently-viewed?contentType=${contentType}&limit=${limit}${assignedOnly ? '&assignedOnly=true' : ''}`,
  );
  if (contentType === 'exercise') {
    const data = response?.data.map((exercise) =>
      mapExerciseFromApi(exercise.content as ApiExercise, locale),
    );
    return { ...response, data, total: data?.length ?? 0 };
  } else if (contentType === 'workout') {
    const data = response?.data.map((workout) =>
      mapWorkoutFromApi(workout.content as ApiWorkout, locale),
    );
    return { ...response, data, total: data?.length ?? 0 };
  } else if (contentType === 'program') {
    const data = response?.data.map((program) =>
      mapProgramFromApi(program.content as ApiProgram, locale),
    );
    return { ...response, data, total: data?.length ?? 0 };
  } else if (contentType === 'meal') {
    const data = response?.data.map((meal) =>
      mapMealFromApi(meal.content as ApiMeal, locale),
    );
    return { ...response, data, total: data?.length ?? 0 };
  } else if (contentType === 'nutrition_plan') {
    const data = response?.data.map((plan) =>
      mapNutritionPlanFromApi(plan.content as ApiNutritionPlan, locale),
    );
    return { ...response, data, total: data?.length ?? 0 };
  }
}
